// Style
import mainStyle from "../mainStyle.module.scss";

// Packages
import React, { useState, useEffect, useMemo } from "react";
import useDataSourceManager from "hooks/useDataSourceManager";
import { isEqual } from "lodash";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Button,
  Typography,
  Tooltip,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Components
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import ModalContainer from "components/ModalContainer";
import { MenuSelect } from "components/Select/menuSelect";
import { StyledBox } from "../components/StyledBox";

// Icons
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as AlertIcon } from "assets/images/icons/icn_board_alert.svg";

// Global const
import {
  formLimitation,
} from "const";

// Parent const
import {
  defaultActions,
  editUuids,
} from "../const";

// Global utils
import {
  calcVlanIdsCountByDevice,
} from "utils/validate";

// Parent utils
import {
  getProfileRequestParams,
  getInitProfileList,
  checkAllowVlans,
  checkPvid,
  getInputStyles,
} from "../helper";

const EditModalAP = ({
  auth,
  alertInfo,
  setAlertInfo,
  defaultConfig,
  setDefaultConfig,
  actions,
  setActions,
  selectedRows,
  showEditGeneral,
}) => {
  const [editUuids] = useState(() => ({
    getProfileList: uuidv4()
  }));

  const theme = useTheme();
  const { sendRequest, getData, dataMap } = useDataSourceManager();

  const [isEdited, setIsEdited] = useState({});

  // 多選時以 portIndex 最小的 port 當主要顯示名稱, 對齊 PortManagement 的行為.
  const selectedPorts = useMemo(
    () => [...selectedRows]
      .sort((a, b) => Number(a.original?.portIndex) - Number(b.original?.portIndex))
      .map(row => row.original),
    [selectedRows]
  );
  const originalSelectedPort = useMemo(() => selectedPorts ? structuredClone(selectedPorts[0]) : {}, [selectedPorts]);
  const [selectedPort, setSelectedPort] = useState(originalSelectedPort ? structuredClone(originalSelectedPort) : {});
  const isNeedCombineModel = useMemo(() => !!selectedPort?.isNeedCombineModel, [selectedPort]);

  const [profileList, setProfileList] = useState([]);
  const selectedProfile = useMemo(() => profileList.find(profile => profile.isSelected), [profileList]);
  const isProfileMode = useMemo(() => !selectedProfile?.isDefault && !!selectedProfile?.value, [selectedProfile?.value]);

  const isEditable = useMemo(() => !selectedProfile || selectedProfile?.isDefault, [selectedProfile]);
  const extraCount = selectedRows.length > 1 ? selectedRows.length - 1 : 0;

  const vlanIdsCount = useMemo(() => calcVlanIdsCountByDevice(defaultConfig.portConfigs, selectedPort), [selectedPort]);
  const isVlanInvalid = useMemo(() => vlanIdsCount.number > 128 || !checkAllowVlans(selectedPort?.allowVlan), [selectedPort?.portType, selectedPort?.allowVlan, vlanIdsCount]);
  const vlanSettingNumber = defaultConfig?.capabilities?.VLAN; // 0: Not support VLAN, 1: Only support single VLAN, 2: Port-Based VLAN

  const getProfileList = () => {
    const { apiUrl, requestParams } = getProfileRequestParams({ auth });

    sendRequest(
      apiUrl,
      editUuids.getProfileList,
      requestParams
    );
  };

  const onApply = () => {

    const { portConfigs } = defaultConfig;
    const portIndexMap = selectedPorts.reduce((acc, port) => {
      acc[port.portIndex] = port;
      return acc;
    }, {});

    const editedPortConfigs = portConfigs.map(port => {
      let resultPort = {};

      if (isNeedCombineModel || !!portIndexMap[port.portIndex]) {
        resultPort = {
          ...port,
          combinePortName: selectedPort.combinePortName,
          portName: isNeedCombineModel ? selectedPort.combinePortName : selectedPort.portName,
          portProfile: selectedPort.portProfile,
          PVID: selectedPort.PVID,
          allowVlan: selectedPort.allowVlan,
        }
      } else if (vlanSettingNumber === 1 && selectedPort.portIndex === 1) {
        // Single VLAN model: all other ports are linked to Port 1
        resultPort = {
          ...port,
          portProfile: selectedPort.portProfile,
          PVID: selectedPort.PVID,
          allowVlan: selectedPort.allowVlan,
        }
      } else {
        resultPort = port;
      }

      return resultPort;
    })

    setDefaultConfig(prevConfig => {
      return {
        ...prevConfig,
        portConfigs: editedPortConfigs,
      }
    })

    setActions({ ...defaultActions, isOpenCreateTemplate: true, isOpenEditModal: false });
  };

  const onCancel = () => {
    setActions({ ...defaultActions, isOpenCreateTemplate: true, isOpenEditModal: false });
  };

  const updatePorts = (field, value) => {
    setSelectedPort((prev) => ({
      ...prev,
      [field]: value
    }));

    if (!isEqual(originalSelectedPort[field], value)) {
      setIsEdited((prev) => ({ ...prev, [field]: true }));
    } else {
      setIsEdited((prev) => {
        const { [field]: removedField, ...rest } = prev;
        return rest;
      });
    }
  };

  const isFormInvalid = () => {
    let isInvalid = false;
    const { portName, PVID, allowVlan } = selectedPort;

    if (portName === "") isInvalid = true;
    if (!checkPvid(PVID?.toString().trim())) isInvalid = true;
    if (isVlanInvalid) isInvalid = true;

    return isInvalid;
  };

  useEffect(() => {
    if (actions?.isOpenEditModal && selectedRows.length > 0) {
      getProfileList();
      setIsEdited({});
    }
  }, [actions?.isOpenEditModal, selectedRows]);

  useEffect(() => {
    if (!selectedPort) return;

    const response = getData(editUuids.getProfileList);
    const responseData = response?.data ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to get the profile list.",
        });
      } else {
        setProfileList(getInitProfileList({
          profileList: responseData,
          selectedPort
        }));
      }
    }
  }, [dataMap?.[editUuids.getProfileList], selectedPort]);

  if (!selectedPorts?.length) return <></>;

  return (
    <>
      <ModalContainer display={actions?.isOpenEditModal} modalWidth="auto">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >

          <Typography
            sx={{
              mb: "30px",
              ml: "20px",
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
            }}
          >
            Selected Ports
          </Typography>

          <Button
            onClick={onCancel}
            sx={{
              marginRight: "-10px",
              marginTop: "-28px",
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>

        </Box>

        <Box
          sx={{ display: "flex", alignItems: "center", ml: "20px", mt: "10px" }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              backgroundColor: theme.palette.primary.subBlue,
              borderRadius: theme.shape.customBorderRadius[0],
              gap: "10px",
              padding: "4px 8px",
            }}
          >
            {selectedPort ? selectedPort.portName : "No ports selected"}
          </Typography>

          {
            extraCount > 0 && (
              <Tooltip
                title={
                  <Box
                    className={mainStyle["custom-scrollbar"]}
                    sx={{
                      maxHeight: 250,
                      overflowY: "auto",
                      pr: 1,
                    }}
                  >
                    {selectedPorts.map(port => {
                      return (
                        <Typography
                          sx={{
                            color: theme.palette.primary.white,
                            fontFamily: theme.typography.fontFamily,
                            fontSize: theme.typography.customFontSize[1],
                            fontWeight: theme.typography.customFontWeight[3],
                            lineHeight: 1.92,
                          }}
                          key={"seriesName_" + port.portIndex}
                        >
                          {port.formattedFullName}
                        </Typography>
                      );
                    })}
                  </Box>
                }
                arrow
              >
                <Typography
                  sx={{
                    ml: 3,
                    color: theme.palette.primary.main,
                    fontSize: theme.typography.customFontSize[10],
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  +{extraCount} Selected
                </Typography>
              </Tooltip>
            )
          }
        </Box>

        <Box
          sx={{ display: "flex", alignItems: "center", ml: "20px", mt: "10px" }}
        >
          <AlertIcon />
          <Box
            sx={{
              ml: "10px",
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[3],
            }}
          >
            Only changes highlighted in{" "}
            <Box
              component="span"
              sx={{
                color: theme.palette.primary.orange,
                fontWeight: "bold",
              }}
            >
              Orange
            </Box>{" "}
            will be applied to all selected ports.
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {selectedPort && (
            <>

              {/* Port Name */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Name
                </Typography>
                <TextField
                  type="text"
                  disabled={!showEditGeneral}
                  value={isNeedCombineModel ? selectedPort?.combinePortName : selectedPort?.portName}
                  onChange={e => {
                    if (isNeedCombineModel) updatePorts("combinePortName", e.target.value);
                    else updatePorts("portName", e.target.value);
                  }}
                  sx={getInputStyles({
                    theme,
                    isInValid: selectedPort?.portName === "",
                    isEdited,
                    field: "portName"
                  })}
                />
                {(vlanSettingNumber === 0 && selectedPort?.portIndex === 5) && (
                  <Typography marginLeft={2} sx={{ fontSize: theme.typography.customFontSize[2], color: theme.palette.primary.black }}>
                    Port 5 (WAN/PoE)
                  </Typography>
                )}
              </StyledBox>

              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Profile
                </Typography>
                <MenuSelect
                  disabled={!showEditGeneral || vlanSettingNumber === 0 || (vlanSettingNumber === 1 && selectedPort?.portIndex !== 1)}
                  value={selectedProfile?.name ?? selectedProfile?.value ?? ""}
                  onChange={opt => {

                    setProfileList(prevProfileList => {
                      return prevProfileList.map(profile => {
                        return {
                          ...profile,
                          isSelected: profile.id === opt.id,
                        }
                      })
                    });

                    updatePorts("portProfile", opt.id);

                    // Sync the profile value or not
                    if (!opt.isDefault) {
                      // Non default profile
                      Object.keys(opt).forEach((key) => {
                        if (key !== "name" && key !== "deviceCategory" && key !== "portProfile") {
                          const newKey = key === "sfpSpeed" ? "comboSfpSpeed" : key;
                          updatePorts(newKey, opt[newKey]);
                        }
                      });
                    } else {
                      // Default profile
                      Object.keys(originalSelectedPort).forEach((key) => {
                        if (key !== "name" && key !== "deviceCategory" && key !== "portProfile") {
                          const newKey = key === "sfpSpeed" ? "comboSfpSpeed" : key;
                          updatePorts(newKey, originalSelectedPort[newKey]);
                        }
                      });
                    }

                  }}
                  options={profileList}
                  width="300px"
                  isEditedField={isEdited.portProfile}
                />
              </StyledBox>

              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  PVID
                </Typography>

                {
                  isProfileMode &&
                  <TextField
                    disabled
                    type="number"
                    value={selectedProfile?.PVID ?? ""}
                    sx={getInputStyles({
                      theme,
                      isInValid: false,
                      isEdited: false,
                      field: "PVID"
                    })}
                    helperText="(1~4094)"
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                    }}
                  />
                }

                {
                  !isProfileMode &&
                  <TextField
                    disabled={!isEditable || !showEditGeneral || vlanSettingNumber === 0 || (vlanSettingNumber === 1 && selectedPort?.portIndex !== 1)}
                    type="number"
                    value={selectedPort?.PVID ?? ""}
                    onChange={e => updatePorts("PVID", e.target.value)}
                    helperText="(1~4094)"
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                    }}
                    onBlur={e => {
                      const num = Number(e.target.value);
                      if (!isNaN(num)) updatePorts("PVID", num);
                    }}
                    sx={getInputStyles({
                      theme,
                      isInValid: !checkPvid(selectedPort?.PVID?.toString().trim()),
                      isEdited,
                      field: "PVID"
                    })}
                  />
                }

              </StyledBox>

              {
                vlanIdsCount.number > 128 &&
                <>
                  <StyledBox sx={{ marginTop: "0" }}>
                    <Typography
                      className={mainStyle["collapse-typography"]}
                      sx={{ alignSelf: "flex-start", pt: "6px" }}
                    >
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.primary.redPrimary,
                        fontSize: "0.75rem",
                      }}
                    >
                      Limit reached: Maximum 128 VLANs allowed.
                    </Typography>

                  </StyledBox>
                </>
              }

              {
                isProfileMode &&
                <StyledBox>
                  <Typography
                    className={mainStyle["collapse-typography"]}
                    sx={{ alignSelf: "flex-start", pt: "6px" }}
                  >
                    Allow VLAN
                  </Typography>

                  <TextField
                    disabled
                    type="text"
                    placeholder="e.g. 1,4-7"
                    helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                    }}
                    value={selectedProfile?.allowVlan}
                    sx={getInputStyles({
                      theme,
                      isInValid: false,
                      isEdited: false,
                      field: "allowVlan"
                    })}
                  />

                </StyledBox>
              }

              {
                !isProfileMode &&
                <StyledBox>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Allow VLANs
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      disabled={!isEditable || !showEditGeneral || vlanSettingNumber === 0 || (vlanSettingNumber === 1 && selectedPort?.portIndex !== 1)}
                      type="text"
                      value={selectedPort?.allowVlan}
                      placeholder="e.g. 1, 4-7"
                      helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                      onChange={e => updatePorts("allowVlan", e.target.value)}
                      sx={getInputStyles({
                        theme,
                        isInValid: isVlanInvalid,
                        isEdited,
                        field: "allowVlan"
                      })}
                    />
                  </Box>

                </StyledBox>
              }

              {
                vlanIdsCount.number > 128 &&
                <>
                  <StyledBox sx={{ marginTop: "0" }}>
                    <Typography
                      className={mainStyle["collapse-typography"]}
                      sx={{ alignSelf: "flex-start", pt: "6px" }}
                    >
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.primary.redPrimary,
                        fontSize: "0.75rem",
                      }}
                    >
                      Limit reached: Maximum 128 VLANs allowed.
                    </Typography>

                  </StyledBox>
                </>
              }

              {(vlanSettingNumber === 1 && selectedPort?.portIndex !== 1) && (
                <StyledBox>
                  <Typography sx={{ fontSize: theme.typography.customFontSize[2], color: theme.palette.primary.orange }}>
                    This port configuration is linked to Port 1. To modify this setting, select Port 1.
                  </Typography>
                </StyledBox>
              )}

            </>
          )}
        </Box>

        <Box
          sx={{
            paddingTop: "20px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <OutlineButton
            sx={{ marginRight: "20px" }}
            width="100px"
            height="32px"
            onClick={onCancel}
          >
            Cancel
          </OutlineButton>
          <SolidButton
            width="100px"
            height="32px"
            disabled={isFormInvalid()}
            onClick={onApply}
          >
            Apply
          </SolidButton>
        </Box>
      </ModalContainer>
    </>
  );
};

export default EditModalAP;
